import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { rhybusWhoHttp } from '../api/rhybus-who-http'
import { ListNames, normalizeIsArchived } from '../config/configuration'
import { getFilesForDetail } from './detailview'
import {
  TT_ANN_TITLE, TT_ANN_DESCRIPTION, TT_ANN_TAGS, TT_ANN_EXPIRY_DATE, TT_COMMON_IS_ARCHIVED, TT_COMMON_ATTACHMENTS,
  TT_KG_TITLE, TT_KG_DESCRIPTION, TT_KG_TAGS,
  TT_QA_QUESTION, TT_QA_DESCRIPTION, TT_QA_ANSWER, TT_QA_TAGS,
  TT_POC_NAME, TT_POC_TITLE, TT_POC_EMAIL, TT_POC_PHONE,
  TT_QL_TITLE, TT_QL_URL, TT_QL_INFO
} from './tooltip-ids'
import { editAttachments } from '../shared/utility/fileHelpers'

// ─── Tooltip ID maps (consumed by enrichConfigTooltips in Settings) ───────────
/** Maps each Announcements form field key to its ToolTipID. */
export const announcementsTooltipIds: Record<string, string> = {
  Title: TT_ANN_TITLE,
  Description: TT_ANN_DESCRIPTION,
  Tags: TT_ANN_TAGS,
  ExpiryDate: TT_ANN_EXPIRY_DATE,
  AttachmentFiles: TT_COMMON_ATTACHMENTS,
  IsArchived: TT_COMMON_IS_ARCHIVED
}

/** Maps each Knowledge Base form field key to its ToolTipID. */
export const knowledgeGraphTooltipIds: Record<string, string> = {
  Title: TT_KG_TITLE,
  Description: TT_KG_DESCRIPTION,
  Tags: TT_KG_TAGS,
  AttachmentFiles: TT_COMMON_ATTACHMENTS,
  IsArchived: TT_COMMON_IS_ARCHIVED
}

/** Maps each Q&A form field key to its ToolTipID. */
export const qaTooltipIds: Record<string, string> = {
  Question: TT_QA_QUESTION,
  Description: TT_QA_DESCRIPTION,
  Answer: TT_QA_ANSWER,
  Tags: TT_QA_TAGS,
  AttachmentFiles: TT_COMMON_ATTACHMENTS,
  IsArchived: TT_COMMON_IS_ARCHIVED
}

/** Maps each Point of Contact form field key to its ToolTipID. */
export const pointOfContactTooltipIds: Record<string, string> = {
  ContactName: TT_POC_NAME,
  ContactTitle: TT_POC_TITLE,
  ContactEmail: TT_POC_EMAIL,
  ContactPhone: TT_POC_PHONE,
  IsArchived: TT_COMMON_IS_ARCHIVED
}

/** Maps each Quick Links form field key to its ToolTipID. */
export const quickLinksTooltipIds: Record<string, string> = {
  Title: TT_QL_TITLE,
  URL: TT_QL_URL,
  Info: TT_QL_INFO,
  IsArchived: TT_COMMON_IS_ARCHIVED
}

// ----------------------------------------------------------------
// TAB KEY TYPE
// ----------------------------------------------------------------

export type ContentManagementTabKey = 'announcements' | 'eventcalendar' | 'knowledge-base' | 'qa' | 'point-of-contact' | 'quick-links' | 'Policy Memos & Guidelines'

// ----------------------------------------------------------------
// ITEM TYPES
// ----------------------------------------------------------------

export type AttachmentFileItem = {
  FileName: string
  ServerRelativeUrl?: string
  name?: string
}

export type MetadataItem = {
  ID: number
  title: string
  Description: string
  Modified?: string
  IsArchived: any
}

export type KnowledgeGraphItem = MetadataItem & {
  AttachmentFiles: AttachmentFileItem[]
  tags: string
}

export type QaItem = {
  ID: number
  question: string
  Description: string
  Answer: string
  AttachmentFiles: AttachmentFileItem[]
  tags: string
  Modified?: string
  IsArchived: any
}

export type AnnouncementItem = {
  ID: number
  Title: string
  Description: string
  AttachmentFiles: AttachmentFileItem[]
  Tags: string
  ExpiryDate: string
  Modified?: string
  IsArchived: any
  ItemModified?: any
  ItemCreated?: any
  ItemCreatedBy?: any
  ItemModifiedBy?: any
}

export type PointOfContactItem = {
  ID: number
  ContactName: string
  ContactTitle: string
  ContactEmail: string
  ContactPhone?: string
  Modified?: string
  IsArchived: any
}

export type QuickLinkItem = {
  ID: number
  Title: string
  Info: string
  URL: string
  Modified?: string
  IsArchived: any
}

export type CalendarEventItem = {
  ID: number
  Id: number
  Title: string
  title: string
  StartDate: string
  EndDate: string | null
  RRule: string
  Location: string
  location: string
  Description: string
  Category: string
  IsAllDayEvent: boolean
  Recurrence: boolean
  AttachmentFiles: Array<{ FileName: string; ServerRelativeUrl: string }>
  DateRange: number | null
}

export const getContentAuditFields = async (itemCreated?: string) => {
  const me = await rhybusWhoHttp().Me()
  const userId = me.displayName ?? me.profileName ?? me.email
  const now = new Date().toISOString()

  return {
    ItemCreatedBy: userId,
    ItemModifiedBy: userId,
    ItemCreated: itemCreated ?? now,
    ItemModified: now,
  }
}
// ----------------------------------------------------------------
// LIVE API — ANNOUNCEMENTS
// ----------------------------------------------------------------

const normalizeContentPayload = <T extends Record<string, any>>(data: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ) as Partial<T>

export const getAnnouncementItems = async (): Promise<AnnouncementItem[]> => {
  const list = await rhybusRListsHttp().getList(ListNames.ANNOUNCEMENT)
  const items = [...(await list.query().orderBy([{ orderBy: 'ItemModified', ascending: false }]).get<any>())]
  const mapped: any[] = items
    .filter((item: any) => !item.IsDeleted)
    .map((item: any) => ({
      ...item,
      ID: item.ID ?? item.Id ?? item._Id ?? item.eKey,
      Id: item.Id ?? item.ID ?? item._Id ?? item.eKey,
      AttachmentFiles: item.AttachmentFiles ?? [],
      Tags: item.Tags ?? '',
      IsArchived: normalizeIsArchived(item.IsArchived),
      ItemModifiedBy: typeof item.ItemModifiedBy === 'string'
        ? { Title: item.ItemModifiedBy }
        : item.ItemModifiedBy ?? { Title: 'System' },
    }))
  await Promise.all(mapped.map(async (item) => {
    if (!item.eKey) return
    try {
      const fileStructure = await getFilesForDetail(item.eKey, 'announcements')
      const blobFiles = (fileStructure?.allFilesList ?? [])
        .filter((f: any) => f.metadata?.blobContainerName === 'announcements')
        .map((f: any) => ({
          ...f,
          FileName: f.Name || f.metadata?.name || '',
          name: f.Name || f.metadata?.name || '',
        }))
      if (blobFiles.length > 0) item.AttachmentFiles = blobFiles
    } catch { /* keep existing AttachmentFiles on error */ }
  }))
  return mapped
}

const stripAnnouncementNonListFields = (data: Partial<AnnouncementItem>) => {
  const {
    AttachmentFiles: _af,
    ID: _id,
    Id: _Id,
    Modified: _mod,
    ItemModifiedBy: _imb,
    ItemCreatedBy: _icb,
    ...rest
  } = data as any
  return rest
}

export const saveAnnouncementItem = async (data: Partial<AnnouncementItem>): Promise<string | undefined> => {
  const auditFields = await getContentAuditFields()
  const result = await (await rhybusRListsHttp().getList(ListNames.ANNOUNCEMENT)).addOrUpdateItem({
      ...normalizeContentPayload(stripAnnouncementNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      IsDeleted: false,
      ...auditFields,
    } as any) as any
  return result?.eKey
}

export const editAnnouncementItem = async (data: Partial<AnnouncementItem>): Promise<string | undefined> => {
  const { eKey, eTag } = data as any
  await editAttachments(eKey,(data.AttachmentFiles as any[]) || [],'announcements')
  const auditFields = await getContentAuditFields(data.ItemCreated)
  await (await rhybusRListsHttp().getList(ListNames.ANNOUNCEMENT)).addOrUpdateItem({
      ...normalizeContentPayload(stripAnnouncementNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      IsDeleted: false,
      ...auditFields,
      eKey,
      eTag,
    } as any)
  return eKey
}

export const removeAnnouncementItem = async (data: Partial<AnnouncementItem>): Promise<void> => {
  const { eKey, eTag } = data as any
  const auditFields = await getContentAuditFields(data.ItemCreated)
  await (await rhybusRListsHttp().getList(ListNames.ANNOUNCEMENT)).addOrUpdateItem({
      ...normalizeContentPayload(stripAnnouncementNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      IsDeleted: true,
      ...auditFields,
      eKey,
      eTag,
    } as any)
}

// ----------------------------------------------------------------
// LIVE API — CALENDAR
// ----------------------------------------------------------------
const validateCalendarDate = (dateValue: any): string | null => {
  if (!dateValue || dateValue === 'undefined' || dateValue === 'null') return null
  const dateStr = String(dateValue).trim()
  if (!dateStr || dateStr === 'undefined' || dateStr === 'null') return null
  return isNaN(new Date(dateStr).getTime()) ? null : dateStr
}

const validateCalendarRRule = (rrule: any): any | null => {
  if (!rrule) return null
  if (typeof rrule === 'string') return rrule.trim() === '' ? null : rrule
  if (typeof rrule === 'object') {
    if (rrule.dtstart === 'undefined' || rrule.dtstart === undefined) return null
    if (rrule.until === 'undefined') return { ...rrule, until: undefined }
  }
  return rrule
}

export const getCalendarEventItems = async (): Promise<CalendarEventItem[]> => {
  try {
    const events = [...(await (await rhybusRListsHttp().getList(ListNames.CALENDAR)).query().get<any>())]
    const mapped = events.map((item: any) => {
      const title = item.Title ?? item.title ?? ''
      const location = item.Location ?? item.location ?? ''
      return {
        ...item,
        ID: item.ID ?? item.Id ?? item._Id ?? item.eKey,
        Id: item.Id ?? item.ID ?? item._Id ?? item.eKey,
        Title: title,
        title: title,
        Location: location,
        location: location,
        Description: item.Description ?? '',
        FreeBusy: item.RRule ?? null,
        EventDate: item.StartDate ?? item.EventDate ?? null,
        start: item.StartDate ?? item.EventDate ?? null,
        fAllDayEvent: item.IsAllDayEvent ?? false,
        fRecurrence: item.Recurrence ?? false,
        AttachmentFiles: item.AttachmentFiles ?? [],
      }
    })
    await Promise.all(mapped.map(async (item: any) => {
      if (!item.eKey) return
      try {
        const fileStructure = await getFilesForDetail(item.eKey, 'eventcalendar')
        const blobFiles = (fileStructure?.allFilesList ?? [])
          .filter((f: any) => f.metadata?.blobContainerName === 'eventcalendar')
          .map((f: any) => ({
            ...f,
            FileName: f.Name || f.metadata?.name || '',
            name: f.Name || f.metadata?.name || '',
          }))
        if (blobFiles.length > 0) item.AttachmentFiles = blobFiles
      } catch { /* keep existing AttachmentFiles on error */ }
    }))
    return mapped as CalendarEventItem[]
  } catch (error) {
    throw error
  }
}

export const saveCalendarEventItem = async (eventData: any, _files: any): Promise<string | undefined> => {
  try {
    const startDate = validateCalendarDate(eventData.StartDate)
    const endDate = validateCalendarDate(eventData.EndDate)
    const rrule = validateCalendarRRule(eventData.rrule)

    if (!startDate) {
      throw new Error('Event start date is required and must be a valid date')
    }

    const auditFields = await getContentAuditFields()

    const newItem = {
      Title: String(eventData.Title ?? eventData.title ?? 'Untitled Event').trim() || 'Untitled Event',
      Location: String(eventData.Location ?? eventData.location ?? '').trim() || null,
      Recurrence: eventData.Recurrence ?? false,
      IsAllDayEvent: eventData.IsAllDayEvent ?? false,
      RRule: rrule,
      StartDate: startDate,
      EndDate: endDate,
      Description: String(eventData.Description || '').trim() || null,
      Category: String(eventData.Category || '').trim() || null,
      DateRange: eventData.DateRange ?? null,
      ...auditFields
    }

    const result = await (await rhybusRListsHttp().getList(ListNames.CALENDAR)).addOrUpdateItem({
        ...newItem
      } as any) as any
    return result?.eKey
  } catch (error) {
    throw error
  }
}

export const editCalendarEventItem = async (eventData: any, _files: any): Promise<string | undefined> => {
  try {
    const startDate = validateCalendarDate(eventData.StartDate)
    const endDate = validateCalendarDate(eventData.EndDate)
    const rrule = validateCalendarRRule(eventData.rrule)

    if (!startDate) {
      throw new Error('Event start date is required and must be a valid date')
    }

    const auditFields = await getContentAuditFields(eventData.ItemCreated ?? eventData.Modified)
    const itemKey = eventData.eKey ?? eventData.Id ?? eventData.ID

    const newItem = {
      ...(itemKey ? { eKey: itemKey } : {}),
      ...(eventData.eTag ? { eTag: eventData.eTag } : {}),
      Title: String(eventData.title ?? eventData.Title ?? 'Untitled Event').trim() || 'Untitled Event',
      Location: String(eventData.Location ?? eventData.location ?? '').trim() || null,
      Recurrence: eventData.Recurrence ?? false,
      IsAllDayEvent: eventData.IsAllDayEvent ?? false,
      RRule: rrule,
      StartDate: startDate,
      EndDate: endDate,
      Description: String(eventData.Description || '').trim() || null,
      Category: String(eventData.Category || '').trim() || null,
      DateRange: eventData.DateRange ?? null,
      ...auditFields
    }

    await (await rhybusRListsHttp().getList(ListNames.CALENDAR)).addOrUpdateItem({
        ...newItem
      } as any)
    return itemKey
  } catch (error) {
    throw error
  }
}

export const removeCalendarEventItem = async (data: Partial<CalendarEventItem>): Promise<void> => {
  await (await rhybusRListsHttp().getList(ListNames.CALENDAR)).deleteItem((data as any).eKey, (data as any).eTag)
}

// ----------------------------------------------------------------
// LIVE API — Knowledge Base
// ----------------------------------------------------------------

export const getKnowledgeGraphItems = async (): Promise<KnowledgeGraphItem[]> => {
  const list = await rhybusRListsHttp().getList(ListNames.KNOWLEDGEGRAPH)
  const items = [...(await list.query().orderBy([{ orderBy: 'ItemModified', ascending: false }]).get<any>())]
  const mapped = items.map((item: any) => ({
    ...item,
    ID: item.ID ?? item.Id ?? item._Id ?? item.eKey,
    Id: item.Id ?? item.ID ?? item._Id ?? item.eKey,
    AttachmentFiles: item.AttachmentFiles ?? [],
    Tags: item.Tags ?? '',
    IsArchived: normalizeIsArchived(item.IsArchived),
    ItemModifiedBy: typeof item.ItemModifiedBy === 'string'
      ? { Title: item.ItemModifiedBy }
      : item.ItemModifiedBy ?? { Title: 'System' },
  }))
  await Promise.all(mapped.map(async (item) => {
    if (!item.eKey) return
    try {
      const fileStructure = await getFilesForDetail(item.eKey, 'knowledge-base')
      const blobFiles = (fileStructure?.allFilesList ?? [])
        .filter((f: any) => f.metadata?.blobContainerName === 'knowledge-base')
        .map((f: any) => ({ ...f, FileName: f.metadata?.name || f.Name || '', name: f.metadata?.name || f.Name || '' }))
      if (blobFiles.length > 0) item.AttachmentFiles = blobFiles
    } catch { /* keep existing AttachmentFiles on error */ }
  }))
  return mapped
}

const stripKnowledgeGraphNonListFields = (data: any) => {
  const { AttachmentFiles: _af, ID: _id, Id: _Id, Modified: _mod, ItemModifiedBy: _imb, ItemCreatedBy: _icb, ...rest } = data
  return rest
}

export const saveKnowledgeGraphItem = async (data: Partial<KnowledgeGraphItem>): Promise<string | undefined> => {
  const auditFields = await getContentAuditFields()
  const result = await (await rhybusRListsHttp().getList(ListNames.KNOWLEDGEGRAPH)).addOrUpdateItem({
      ...normalizeContentPayload(stripKnowledgeGraphNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      ...auditFields,
    } as any) as any
  return result?.eKey
}

export const editKnowledgeGraphItem = async (data: Partial<KnowledgeGraphItem>): Promise<string | undefined> => {
  const { eKey, eTag } = data as any
  await editAttachments(eKey,(data.AttachmentFiles as any[]) || [],'knowledge-base')
  const auditFields = await getContentAuditFields((data as any).ItemCreated)
  await (await rhybusRListsHttp().getList(ListNames.KNOWLEDGEGRAPH)).addOrUpdateItem({
      ...normalizeContentPayload(stripKnowledgeGraphNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      ...auditFields,
      eKey,
      eTag,
    } as any)
  return eKey
}

export const removeKnowledgeGraphItem = async (data: Partial<KnowledgeGraphItem>): Promise<void> => {
  await (await rhybusRListsHttp().getList(ListNames.KNOWLEDGEGRAPH)).deleteItem((data as any).eKey, (data as any).eTag)
}

// ----------------------------------------------------------------
// LIVE API — Q&A
// ----------------------------------------------------------------

export const getQaItems = async (): Promise<QaItem[]> => {
  const list = await rhybusRListsHttp().getList(ListNames.QANDA)
  const items = [...(await list.query().orderBy([{ orderBy: 'ItemModified', ascending: false }]).get<any>())]
  const mapped = items.map((item: any) => ({
    ...item,
    ID: item.ID ?? item.Id ?? item._Id ?? item.eKey,
    Id: item.Id ?? item.ID ?? item._Id ?? item.eKey,
    AttachmentFiles: item.AttachmentFiles ?? [],
    Tags: item.Tags ?? '',
    IsArchived: normalizeIsArchived(item.IsArchived),
    ItemModifiedBy: typeof item.ItemModifiedBy === 'string'
      ? { Title: item.ItemModifiedBy }
      : item.ItemModifiedBy ?? { Title: 'System' },
  }))
  await Promise.all(mapped.map(async (item) => {
    if (!item.eKey) return
    try {
      const fileStructure = await getFilesForDetail(item.eKey, 'qanda')
      const blobFiles = (fileStructure?.allFilesList ?? [])
        .filter((f: any) => f.metadata?.blobContainerName === 'qanda')
        .map((f: any) => ({ ...f, FileName: f.metadata?.name || f.Name || '', name: f.metadata?.name || f.Name || '' }))
      if (blobFiles.length > 0) item.AttachmentFiles = blobFiles
    } catch { /* keep existing AttachmentFiles on error */ }
  }))
  return mapped
}

const stripQaNonListFields = (data: any) => {
  const { AttachmentFiles: _af, ID: _id, Id: _Id, Modified: _mod, ItemModifiedBy: _imb, ItemCreatedBy: _icb, Title: _title, ...rest } = data
  return rest
}

export const saveQaItem = async (data: Partial<QaItem>): Promise<string | undefined> => {
  const auditFields = await getContentAuditFields()
  const result = await (await rhybusRListsHttp().getList(ListNames.QANDA)).addOrUpdateItem({
      ...normalizeContentPayload(stripQaNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      ...auditFields,
    } as any) as any
  return result?.eKey
}

export const editQaItem = async (data: Partial<QaItem>): Promise<string | undefined> => {
  const { eKey, eTag } = data as any
  await editAttachments(eKey,(data.AttachmentFiles as any[]) || [],'qanda')
  const auditFields = await getContentAuditFields((data as any).ItemCreated)
  await (await rhybusRListsHttp().getList(ListNames.QANDA)).addOrUpdateItem({
      ...normalizeContentPayload(stripQaNonListFields(data)),
      IsArchived: normalizeIsArchived(data.IsArchived),
      ...auditFields,
      eKey,
      eTag,
    } as any)
  return eKey
}

export const removeQaItem = async (data: Partial<QaItem>): Promise<void> => {
  await (await rhybusRListsHttp().getList(ListNames.QANDA)).deleteItem((data as any).eKey, (data as any).eTag)
}

// ----------------------------------------------------------------
// LIVE API — POINT OF CONTACT
// ----------------------------------------------------------------

export const getPOCItems = async (): Promise<PointOfContactItem[]> =>
  [...(await (await rhybusRListsHttp().getList(ListNames.POINT_OF_CONTACT)).query().get<PointOfContactItem>())]
    .map((item: any) => ({
      ...item,
      IsArchived: normalizeIsArchived(item.IsArchived),
      ContactName: item.PointOfContact?.trim() ?? item.ContactName ?? '',
      ContactEmail: item.Title ?? item.ContactEmail ?? '',
      ContactPhone: item.PhoneNumber ?? item.ContactPhone ?? '',
    }))

export const savePOCItem = async (data: any): Promise<void> => {
  const auditFields = await getContentAuditFields()
  await (await rhybusRListsHttp().getList(ListNames.POINT_OF_CONTACT)).addOrUpdateItem({
    ...normalizeContentPayload({
      PointOfContact: data?.ContactName?.DisplayText,
      Title: data?.ContactName?.email,
      PhoneNumber: data?.ContactPhone,
      DutyTitle: data?.ContactName?.DisplayText,
      ...auditFields
    }),
    IsArchived: normalizeIsArchived(data?.IsArchived)
  } as any)
}

export const editPOCItem = async (data: Partial<PointOfContactItem>): Promise<void> => {
  const { eKey, eTag } = data as any
  const auditFields = await getContentAuditFields((data as any).ItemCreated)
  const contact = data.ContactName as any
  const isPickerObject = contact && typeof contact === 'object' && 'DisplayText' in contact
  await (await rhybusRListsHttp().getList(ListNames.POINT_OF_CONTACT)).addOrUpdateItem({
    PointOfContact: isPickerObject ? contact.DisplayText : contact,
    Title: isPickerObject ? contact.email : data.ContactEmail,
    DutyTitle: isPickerObject ? contact.designation ?? contact.DisplayText : contact,
    IsArchived: normalizeIsArchived(data.IsArchived),
    PhoneNumber: data?.ContactPhone,
    ...auditFields,
    eKey,
    eTag,
  } as any)
}

export const removePOCItem = async (data: Partial<PointOfContactItem>): Promise<void> => {
  await (await rhybusRListsHttp().getList(ListNames.POINT_OF_CONTACT)).deleteItem((data as any).eKey, (data as any).eTag)
}

// ----------------------------------------------------------------
// LIVE API — QUICK LINKS
// ----------------------------------------------------------------

export const getQuickLinkItems = async (): Promise<QuickLinkItem[]> =>
  [...(await (await rhybusRListsHttp().getList(ListNames.QUICK_LINK)).query().orderBy([{ orderBy: 'ItemModified', ascending: false }]).get<QuickLinkItem>())]
    .map((item: any) => ({
      ...item,
      ID: item.ID ?? item.Id ?? item._Id ?? item.eKey,
      Id: item.Id ?? item.ID ?? item._Id ?? item.eKey,
      IsArchived: normalizeIsArchived(item.IsArchived),
      ItemModifiedBy: typeof item.ItemModifiedBy === 'string'
        ? { Title: item.ItemModifiedBy }
        : item.ItemModifiedBy ?? { Title: 'System' },
    }))

const stripQuickLinkNonListFields = (data: any) => {
  const { AttachmentFiles: _af, ID: _id, Id: _Id, Modified: _mod, ItemModifiedBy: _imb, ItemCreatedBy: _icb, ...rest } = data
  return rest
}

export const saveQuickLinkItem = async (data: Partial<QuickLinkItem>): Promise<void> => {
  const auditFields = await getContentAuditFields()
  await (await rhybusRListsHttp().getList(ListNames.QUICK_LINK)).addOrUpdateItem({
    ...normalizeContentPayload(stripQuickLinkNonListFields(data)),
    IsArchived: normalizeIsArchived(data.IsArchived),
    ...auditFields,
  } as any)
}

export const editQuickLinkItem = async (data: Partial<QuickLinkItem>): Promise<void> => {
  const { eKey, eTag } = data as any
  const auditFields = await getContentAuditFields((data as any).ItemCreated)
  await (await rhybusRListsHttp().getList(ListNames.QUICK_LINK)).addOrUpdateItem({
    ...normalizeContentPayload(stripQuickLinkNonListFields(data)),
    IsArchived: normalizeIsArchived(data.IsArchived),
    ...auditFields,
    eKey,
    eTag,
  } as any)
}

export const removeQuickLinkItem = async (data: Partial<QuickLinkItem>): Promise<void> => {
  await (await rhybusRListsHttp().getList(ListNames.QUICK_LINK)).deleteItem((data as any).eKey, (data as any).eTag)
}
